import type { IconName } from '@/lib/icones-police';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import type { CodeLibelle } from '@rosetta/shared';

/**
 * How a status of the control shows (#10): only the mention is in colour, never the card (a
 * coloured card would read as a noyau). "Bloquant" in `rouge-deep` with `block`, "À relire" in
 * `jaune-deeper` with `visibility` (5.6:1 on white, 4.7:1 on `gray-100`: the hover of a counter, #30), "OK" in
 * `gray-700` with `check`; any other status of Tahiti in `gray-700` with `info`.
 */
const STYLES: Readonly<Record<string, { icone: IconName; couleur: string }>> = {
  bloquant: { icone: 'block', couleur: 'text-rouge-deep' },
  a_relire: { icone: 'visibility', couleur: 'text-jaune-deeper' },
  ok: { icone: 'check', couleur: 'text-gray-700' },
};

const AUTRE = { icone: 'info', couleur: 'text-gray-700' } as const;

/** The icon and the colour of a status of Tahiti. */
export function styleStatut(code: string): { icone: IconName; couleur: string } {
  return STYLES[code] ?? AUTRE;
}

interface MentionStatutProps {
  statut: CodeLibelle;
  className?: string;
}

/** The status of an indicator, as its card shows it: icon and label of Tahiti, in its colour. */
export function MentionStatut({ statut, className }: MentionStatutProps) {
  const { icone, couleur } = styleStatut(statut.code);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption leading-[1.4]',
        couleur,
        className,
      )}
    >
      <Icon name={icone} size={16} />
      {statut.libelle}
    </span>
  );
}
